import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { toBirthDate } from '@/lib/patient/age';

export type Gender = 'male' | 'female';

/** What a sign-up or profile form holds: blanks allowed, date as 'YYYY-MM-DD'. */
export interface DemographicsDraft {
  gender: Gender | '';
  dateOfBirth: string;
}

export const emptyDemographics: DemographicsDraft = { gender: '', dateOfBirth: '' };

export interface PatientDemographics {
  gender: Gender | null;
  dateOfBirth: Date | null;
}

/**
 * Gender and date of birth live once on users/{uid} (same field names and
 * values as treatment requests), so the exam, the questionnaire and both
 * apps all read the same answer.
 */
export async function getPatientDemographics(userId: string): Promise<PatientDemographics> {
  const snap = await getDoc(doc(db, 'users', userId));
  const data = snap.data() || {};
  const gender = data.gender === 'male' || data.gender === 'female' ? data.gender : null;
  return { gender, dateOfBirth: toBirthDate(data.dateOfBirth) };
}

export async function savePatientDemographics(userId: string, value: { gender: Gender; dateOfBirth: Date }): Promise<void> {
  await updateDoc(doc(db, 'users', userId), {
    gender: value.gender,
    dateOfBirth: Timestamp.fromDate(value.dateOfBirth),
    updatedAt: Timestamp.now(),
  });
}

/**
 * Whatever of gender / date of birth ('YYYY-MM-DD') the user supplied at
 * sign-up or in their profile; blanks are left untouched.
 */
export async function patchDemographics(userId: string, value: { gender?: Gender | ''; dateOfBirth?: string }): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (value.gender) patch.gender = value.gender;
  const birth = toBirthDate(value.dateOfBirth || null);
  if (birth) patch.dateOfBirth = Timestamp.fromDate(birth);
  if (Object.keys(patch).length === 0) return;
  await updateDoc(doc(db, 'users', userId), { ...patch, updatedAt: Timestamp.now() });
}
